import { Operation as LogOperation, Log, LogCategory } from '@augloop/logging';
import { LogCountLimiter } from '../utils';
// Rate control thresholds for logging purpose
const rpsLoggingThreshold = 50;
const bpsLoggingThreshold = 100000;
// Heartbeat interval
const pingIntervalInMs = 30000;
// Max number of retry attempts for heartbeat
const pingRetryAttemptMax = 3;
export class NetworkRateController {
    constructor(networkRateSettings) {
        var _a, _b, _c, _d, _e;
        this.logCountLimiter = new LogCountLimiter(NetworkRateController.className);
        this.hasBeenOpened = false;
        this.lastPingTime = 0;
        this.lastPongTime = 0;
        this.lastEgressTime = 0;
        this.emptyMessageId = 0;
        this.egressMessageCount = 0;
        this.egressByteCount = 0;
        this.prevSeq = -1;
        this.remainingPingFailures = 3;
        this.pendingQueue = [];
        this.egressBytesTotal = 0;
        this.egressRateLogOp = new LogOperation({ operationName: 'WSEgressRate', success: true });
        this.egressLogOp = new LogOperation({ operationName: 'WSEgressControl', success: true }).start();
        this.egressedCache = new Map();
        // Default or use host-provided settings
        this.rpsThreshold = (_a = networkRateSettings === null || networkRateSettings === void 0 ? void 0 : networkRateSettings.rpsThreshold) !== null && _a !== void 0 ? _a : 150;
        this.bpsThreshold = (_b = networkRateSettings === null || networkRateSettings === void 0 ? void 0 : networkRateSettings.bpsThreshold) !== null && _b !== void 0 ? _b : 500000000; //500MB;
        this.pendingMaxSize = (_c = networkRateSettings === null || networkRateSettings === void 0 ? void 0 : networkRateSettings.pendingMaxSize) !== null && _c !== void 0 ? _c : 10000;
        this.egressedMaxSize = (_d = networkRateSettings === null || networkRateSettings === void 0 ? void 0 : networkRateSettings.egressedMaxSize) !== null && _d !== void 0 ? _d : 10000;
        this.egressedBytesMaxSize = (_e = networkRateSettings === null || networkRateSettings === void 0 ? void 0 : networkRateSettings.egressedBytesMaxSize) !== null && _e !== void 0 ? _e : 10000000;
    }
    init(worker, ingress, clientMetadata) {
        this.worker = worker;
        this.ingress = ingress;
        this.clientMetadata = clientMetadata;
        this.egressRateControlTimer = setInterval(() => this.flush(), 1000);
    }
    ingressFromWorker(message, onIngressParsed) {
        if (message.length === 1 && message === NetworkRateController.pingPongMessage) {
            this.lastPongTime = Date.now();
        }
        else {
            this.ingress(message, (response) => {
                onIngressParsed === null || onIngressParsed === void 0 ? void 0 : onIngressParsed(response);
                this.updateEgressedCache(response);
            });
        }
    }
    open() {
        this.collectTelemetry(true /*success*/, 'OnOpen');
        this.hasBeenOpened = true;
        this.egressRateControlIntervalStart = Date.now();
        // Initial ping
        this.ping();
        this.collectTelemetry(true /*success*/, 'ping', 'initial ping');
        // Flush pending messages
        this.flush();
        /*
        The ping-pong message serves as heartbeat detector of the client-server channel. It is set up
        (one time) when network connection has been established, and runs every pingIntervalInMs.
        Due to its periodicity, if a pong message does not arrive before the next time around, it is
        an indication that we may be losing the connection, upon which a couple of retries will be
        attempted before finally giving up and close.

        Note:
        a. No need to ping if no messages are being sent
        b. Start ping again if more messages are being sent
        -------C--------30s---------C--------30s---------C--------30s---------C-------30s------->
        a.   ping    msg  pong
        b.   ping    msg  pong                     msg  ping   msg msg pong
        */
        this.pingInterval = setInterval(() => {
            this.hasBeenOpened = (this.lastPongTime >= this.lastPingTime) && (this.lastPongTime - this.lastPingTime <= pingIntervalInMs);
            // Skip pinging if no messages are being sent
            if (this.lastPongTime > this.lastEgressTime) {
                return;
            }
            // Collect ping-pong round trip time
            const pingPongRoundTrip = this.lastPongTime - this.lastPingTime;
            if (this.hasBeenOpened) {
                // Reset retry attempt allowed
                this.remainingPingFailures = pingRetryAttemptMax;
                this.collectTelemetry(true /*success*/, 'ping', 'WebSocket channel active', [`${pingPongRoundTrip}`, `${this.remainingPingFailures}`]);
                this.ping();
            }
            else {
                if (this.remainingPingFailures > 0) {
                    --this.remainingPingFailures;
                    this.collectTelemetry(true /*success*/, 'ping', `Pong not received within ${pingIntervalInMs} ms`, [`${pingPongRoundTrip}`, `${this.remainingPingFailures}`]);
                    this.ping();
                }
                else {
                    this.collectTelemetry(false /*success*/, 'ping', `Pong still not received after all retry attempts`, [`${pingPongRoundTrip}`, `${this.remainingPingFailures}`]);
                    // Stop pinging and disconnect
                    this.close();
                }
            }
        }, pingIntervalInMs);
    }
    close() {
        this.collectTelemetry(true /*success*/, 'OnClose');
        this.worker = undefined;
        this.prevSeq = -1;
        this.hasBeenOpened = false;
        this.lastPingTime = 0;
        this.lastPongTime = 0;
        this.clearIntervalAndTimeout();
    }
    clearMessageQueues() {
        this.pendingQueue = [];
        this.egressedCache.clear();
    }
    clearIntervalAndTimeout() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = undefined;
        }
        if (this.egressRateControlTimer) {
            clearInterval(this.egressRateControlTimer);
            this.egressRateControlTimer = undefined;
        }
    }
    ping() {
        this.lastPingTime = Date.now();
        if (this.worker) {
            this.worker.egress({ obj: NetworkRateController.pingPongMessage });
        }
    }
    updateEgressedCache(response) {
        if (response.messageId && this.egressedCache.has(response.messageId)) {
            this.egressBytesTotal -= this.egressedCache.get(response.messageId);
            this.egressedCache.delete(response.messageId);
        }
    }
    collectTelemetry(success, signature, description, dimensions) {
        var _a, _b, _c, _d;
        this.egressLogOp.start();
        //TODO: ADO 8015439 - Add networkMode to telemetry to distinguish between WebSocket and Http
        this.egressLogOp.resultSignature = signature !== null && signature !== void 0 ? signature : '';
        this.egressLogOp.resultDescription = description !== null && description !== void 0 ? description : '';
        this.egressLogOp.success = success;
        if (dimensions) {
            this.egressLogOp.dimension0 = (_a = dimensions[0]) !== null && _a !== void 0 ? _a : '';
            this.egressLogOp.dimension1 = (_b = dimensions[1]) !== null && _b !== void 0 ? _b : '';
            this.egressLogOp.dimension2 = (_c = dimensions[2]) !== null && _c !== void 0 ? _c : '';
            this.egressLogOp.dimension3 = (_d = dimensions[3]) !== null && _d !== void 0 ? _d : '';
        }
        else {
            this.egressLogOp.dimension0 = '';
            this.egressLogOp.dimension1 = '';
            this.egressLogOp.dimension2 = '';
            this.egressLogOp.dimension3 = '';
        }
        this.logCountLimiter.log(() => Log.info(0x1e545711 /* tag_4vf2r */, LogCategory.CoreDefault, this.egressLogOp.stop()));
    }
    /**
     * Receives message to be sent from upstream. Send it if possible, else put it in pending queue
     *
     * @param message message to be sent, containing seqId, messageId, and isSessionInitMessage flag
     * @returns None
     */
    egress(message) {
        var _a;
        if ((message.obj instanceof ArrayBuffer || message.isHttpSessionInitMessage) && this.worker) {
            this.worker.egress(message);
            return;
        }
        this.validateSyncMessage(message.messageId, message.seqId);
        const isEgressRateExceeded = this.egressMessageCount >= this.rpsThreshold || this.egressByteCount >= this.bpsThreshold;
        const isEgressSizeLimitExceeded = this.egressBytesTotal > this.egressedBytesMaxSize || this.pendingQueue.length > this.pendingMaxSize || this.egressedCache.size > this.egressedMaxSize;
        if (!this.hasBeenOpened || isEgressRateExceeded || this.pendingQueue.length > 0 || isEgressSizeLimitExceeded) {
            let signature = '';
            signature += this.pendingQueue.length >= this.pendingMaxSize ? 'Pending queue max exceeded. ' : (this.pendingQueue.length > 0 ? 'Pending queue not empty' : '');
            signature += this.egressedCache.size >= this.egressedMaxSize ? 'Egressed cache max exceeded. ' : '';
            signature += this.egressBytesTotal > this.egressedBytesMaxSize ? 'Egressed bytes max exceeded. ' : '';
            signature += this.egressMessageCount >= this.rpsThreshold ? 'RPS exceeded. ' : '';
            signature += this.egressByteCount >= this.bpsThreshold ? 'BPS exceeded. ' : '';
            signature += !this.hasBeenOpened ? 'WebSocket not opened' : '';
            const description = `pendingQueueSize: ${this.pendingQueue.length} | egressedCacheSize: ${this.egressedCache.size} | egressedBytesTotal: ${this.egressBytesTotal} | rpsThreshold: ${this.rpsThreshold} | bpsThreshold: ${this.bpsThreshold}`;
            this.collectTelemetry(true /*success*/, signature, description);
            // Do not send through network worker yet if any of the conditions is true
            const messageId = (_a = message.messageId) !== null && _a !== void 0 ? _a : `id${this.emptyMessageId++}`;
            this.pendingQueue.push({ obj: message.obj, seqId: message === null || message === void 0 ? void 0 : message.seqId, messageId: messageId, isHttpSessionInitMessage: message === null || message === void 0 ? void 0 : message.isHttpSessionInitMessage });
            return;
        }
        this.sendToWorker(message);
    }
    /**
     * Send the message over the wire to the worker. Also cache it and wait to be acked.
     *
     * @param message message to be sent, also containing the messageId
     */
    sendToWorker(message) {
        try {
            this.worker.egress(message);
            const messageSize = message.obj instanceof ArrayBuffer ? message.obj.byteLength : message.obj.length;
            // Keep track of egress rate
            this.logEgressCount(messageSize);
            this.lastEgressTime = Date.now();
            if (message.messageId && message.messageId.startsWith('c')) {
                this.egressBytesTotal += messageSize;
                this.egressedCache.set(message.messageId, messageSize);
            }
        }
        catch (e) {
            this.collectTelemetry(false /*success*/, 'sendToWorker', e ? e.message : '');
        }
    }
    /**
     * Flush out pending messages
     */
    flush() {
        while (this.hasBeenOpened
            && this.pendingQueue.length > 0
            && this.pendingQueue.length < this.pendingMaxSize
            && this.egressedCache.size < this.egressedMaxSize
            && this.egressMessageCount < this.rpsThreshold
            && this.egressByteCount < this.bpsThreshold
            && this.egressBytesTotal < this.egressedBytesMaxSize) {
            const message = this.pendingQueue.shift();
            this.sendToWorker(message);
        }
    }
    /**
     * Check for sequence gap in Sync messages.
     * The seq is expected to be continuous.
     *
     * @param messageId message id of the sync message
     * @param seq sequence id of the Sync message
     */
    validateSyncMessage(messageId, seqId) {
        // Skip validation if message or seq id is absent or it is a resent message
        if (seqId === undefined || messageId === undefined || seqId <= this.prevSeq) {
            return;
        }
        if (seqId && seqId !== this.prevSeq + 1) {
            this.collectTelemetry(true /*success*/, 'abandonedSyncMessage', 'Gap in sync message', [`${this.prevSeq}`, `${seqId}`, `${seqId - this.prevSeq}`]);
        }
        this.prevSeq = seqId;
    }
    logEgressCount(byteCount) {
        var _a;
        const now = Date.now();
        if (now - this.egressRateControlIntervalStart > 1000) {
            if (this.egressMessageCount > rpsLoggingThreshold || this.egressByteCount > bpsLoggingThreshold) {
                this.egressRateLogOp.start();
                this.egressRateLogOp.resultDescription = this.egressMessageCount > rpsLoggingThreshold ? 'rps limit exceeded' : '';
                this.egressRateLogOp.resultDescription = this.egressByteCount > bpsLoggingThreshold ? 'bps limit exceeded' : '';
                this.egressRateLogOp.dimension0 = (now - this.egressRateControlIntervalStart).toString();
                this.egressRateLogOp.dimension1 = `${this.egressMessageCount}`;
                this.egressRateLogOp.dimension2 = `${this.egressByteCount}`;
                Log.info(0x1e545710 /* tag_4vf2q */, LogCategory.CoreDefault, this.egressRateLogOp.stop());
            }
            if (((_a = this.clientMetadata) === null || _a === void 0 ? void 0 : _a.releaseAudienceGroup) !== 'Production') {
                // Help figuring out whether the default limits are appropriate
                this.collectTelemetry(true /*success*/, '', 'egress', 
                // Collect magnitude of these variables for better log aggregation
                [
                    `${this.pendingQueue.length === 0 ? '0' : this.pendingQueue.length.toString().length}`,
                    `${this.egressedCache.size === 0 ? '0' : this.egressedCache.size.toString().length}`,
                    `${this.egressBytesTotal === 0 ? '0' : this.egressBytesTotal.toString().length}`,
                    `${this.rpsThreshold} | ${this.bpsThreshold}`
                ]);
            }
            this.egressRateControlIntervalStart = now;
            this.egressMessageCount = 0;
            this.egressByteCount = 0;
        }
        this.egressMessageCount++;
        this.egressByteCount += (byteCount !== null && byteCount !== void 0 ? byteCount : 0);
    }
}
NetworkRateController.pingPongMessage = '~';
NetworkRateController.className = 'NetworkRateController';
//# sourceMappingURL=network-rate-controller.js.map